import axios from "axios";
import { currentRoles, isSocietyAdminRole } from "@/utils";

// Base URL mirrors other services (main.js configures axios interceptors globally).
const VUE_APP_BASE_URL =
  localStorage.getItem("CF_BASE_URL") ||
  process.env.VUE_APP_BASE_URL ||
  "https://sass-api-dev.dimitra.dev/api/admin";

export const DASHBOARD_TYPES = { PARENT: "parent", UNION: "union", SOCIETY: "society", FACTORY: "factory" };

/**
 * Decide which dashboard variant the current user gets.
 * Server ultimately auto-scopes by role too; this drives title/identifiers on the client.
 * Dev override: ?dashboard=union|society|factory (dummy-data preview only).
 */
export function resolveDashboardType() {
  const override = new URLSearchParams(window.location.search).get("dashboard");
  if (override && Object.values(DASHBOARD_TYPES).includes(override)) return override;

  const roles = currentRoles();
  // Parent sits above unions (kenya_ptsi) — check first.
  if (roles.includes("kenya_ptsi")) return DASHBOARD_TYPES.PARENT;
  if (roles.includes("naccu_kenya_admin")) return DASHBOARD_TYPES.UNION;
  if (roles.some(isSocietyAdminRole)) return DASHBOARD_TYPES.SOCIETY;
  // Hierarchy: union -> societies -> factories. Factories are app users with no
  // admin login yet, so there is no live factory dashboard. The factory mock +
  // ?dashboard=factory override are kept for when factory admins are introduced.
  return DASHBOARD_TYPES.UNION;
}

export default {
  // Real API (Phase 3). Endpoint auto-scopes by the caller's role/membership.
  getDashboardOverview(params = {}) {
    return axios.get(`${VUE_APP_BASE_URL}/coffee-dashboard/overview`, { params });
  },

  // Society dashboard core metrics + factory dropdown options.
  // params: { factoryId, from, to }
  getSocietyDashboard(params = {}) {
    return axios.get(`${VUE_APP_BASE_URL}/coffee-dashboard/society`, { params });
  },

  // Union (national) dashboard summary (tiles + quality) + society dropdown options.
  // params: { societyId, from, to }
  getUnionDashboard(params = {}) {
    return axios.get(`${VUE_APP_BASE_URL}/coffee-dashboard/union`, { params });
  },

  // Heavy union Receiving-vs-Processing per-society breakdown (lazy-loaded, unfiltered).
  getUnionBreakdown() {
    return axios.get(`${VUE_APP_BASE_URL}/coffee-dashboard/union/breakdown`);
  },

  // Parent (above unions) dashboard summary + union dropdown options.
  // params: { unionId, from, to }
  getParentDashboard(params = {}) {
    return axios.get(`${VUE_APP_BASE_URL}/coffee-dashboard/parent`, { params });
  },

  // Heavy parent Receiving-vs-Processing per-union breakdown (lazy-loaded, unfiltered).
  getParentBreakdown() {
    return axios.get(`${VUE_APP_BASE_URL}/coffee-dashboard/parent/breakdown`);
  },
};

// ---------------------------------------------------------------------------
// TEMPORARY DEV MOCKS — remove once backend endpoint is live (Phase 3).
// Each shape IS the contract the backend must return for that scope.
// Generic shape so one view renders all three variants.
// ---------------------------------------------------------------------------
export const MOCK_DASHBOARDS = {
  union: {
    type: "union",
    core: { totalFarmers: 12840, totalFarms: 15320, totalReceivingKg: 4820000, totalProcessingKg: 3610000 },
    identifiers: { unionNumbers: ["UN-KE-001", "UN-KE-002", "UN-KE-003"] },
    // Union is the cooperative; its children are societies, whose children are factories.
    operational: { societies: 42, mills: 9, crops: 6, bushes: 3120000 },
    quality: {
      avgScore: 84.3,
      maxScore: 100,
      greenGrades: [
        { grade: "AA", count: 1200 },
        { grade: "AB", count: 2100 },
        { grade: "PB", count: 900 },
        { grade: "C", count: 640 },
        { grade: "TT", count: 300 },
      ],
    },
    breakdown: {
      by: "society",
      entities: [
        { name: "Kiambu Society", receivingKg: 620000, processingKg: 470000 },
        { name: "Nyeri Society", receivingKg: 810000, processingKg: 640000 },
        { name: "Muranga Society", receivingKg: 540000, processingKg: 410000 },
        { name: "Kirinyaga Society", receivingKg: 720000, processingKg: 560000 },
        { name: "Embu Society", receivingKg: 430000, processingKg: 320000 },
      ],
    },
  },
  society: {
    type: "society",
    core: { totalFarmers: 1420, totalFarms: 1680, totalReceivingKg: 540000, totalProcessingKg: 410000 },
    identifiers: { societyNumbers: ["SOC-KE-014"], factoryNumbers: ["FAC-021", "FAC-022"] },
    // Society sits under the union; its children are factories.
    operational: { mills: 2, crops: 4, bushes: 348000 },
    quality: {
      avgScore: 82.1,
      maxScore: 100,
      greenGrades: [
        { grade: "AA", count: 180 },
        { grade: "AB", count: 260 },
        { grade: "PB", count: 120 },
        { grade: "C", count: 80 },
      ],
    },
    breakdown: {
      by: "factory",
      entities: [
        { name: "Factory 021", receivingKg: 300000, processingKg: 230000 },
        { name: "Factory 022", receivingKg: 240000, processingKg: 180000 },
      ],
    },
  },
  factory: {
    type: "factory",
    core: { totalFarmers: 610, totalFarms: 720, totalReceivingKg: 300000, totalProcessingKg: 230000 },
    identifiers: { factoryNumbers: ["FAC-021"] },
    operational: { cooperatives: 1, crops: 3, bushes: 152000 },
    quality: {
      avgScore: 83.5,
      maxScore: 100,
      greenGrades: [
        { grade: "AA", count: 90 },
        { grade: "AB", count: 140 },
        { grade: "PB", count: 60 },
        { grade: "C", count: 40 },
      ],
    },
    breakdown: {
      by: "crop",
      entities: [
        { name: "Arabica SL28", receivingKg: 160000, processingKg: 125000 },
        { name: "Arabica SL34", receivingKg: 90000, processingKg: 70000 },
        { name: "Ruiru 11", receivingKg: 50000, processingKg: 35000 },
      ],
    },
  },
};
